import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Button,
  Divider,
  IconButton,
} from '@mui/material';
import {
  DirectionsCar,
  SwapHoriz,
  Cancel,
  PersonAdd,
  Assignment,
  MoreVert,
  Refresh,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@mui/material/styles';

interface ActivityItem {
  id: string;
  type: 'booking' | 'swap' | 'cancellation' | 'listing' | 'assignment';
  title: string;
  description: string;
  user: string;
  location: string;
  timestamp: string;
  status: 'completed' | 'pending' | 'cancelled';
  details?: {
    vehicle?: string;
    amount?: string;
    bookingId?: string;
  };
}

export const ActivityFeed: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = React.useState(false);

  // Mock data - in real app, this would come from API
  const activities: ActivityItem[] = [
    {
      id: '1',
      type: 'booking',
      title: 'New Booking',
      description: 'Toyota Camry booked by John D. for 3 days',
      user: 'John D.',
      location: 'Lagos, Nigeria',
      timestamp: '2025-01-08T14:30:00Z',
      status: 'completed',
      details: {
        vehicle: 'Toyota Camry (2023)',
        amount: '$255',
        bookingId: 'BK-8742',
      },
    },
    {
      id: '2',
      type: 'swap',
      title: 'Car Swap',
      description: 'Honda Civic swapped for Toyota Corolla',
      user: 'Sarah K.',
      location: 'Nairobi, Kenya',
      timestamp: '2025-01-08T14:08:00Z',
      status: 'completed',
      details: {
        vehicle: 'Honda Civic → Toyota Corolla',
        bookingId: 'BK-8741',
      },
    },
    {
      id: '3',
      type: 'cancellation',
      title: 'Cancellation',
      description: 'Mercedes GLC booking cancelled by host',
      user: 'Michael T.',
      location: 'Accra, Ghana',
      timestamp: '2025-01-08T13:45:00Z',
      status: 'cancelled',
      details: {
        vehicle: 'Mercedes GLC (2024)',
        amount: '$180',
        bookingId: 'BK-8740',
      },
    },
    {
      id: '4',
      type: 'booking',
      title: 'New Booking',
      description: 'BMW 3 Series booked by Sarah M. for 5 days',
      user: 'Sarah M.',
      location: 'Lagos, Nigeria',
      timestamp: '2025-01-08T13:15:00Z',
      status: 'completed',
      details: {
        vehicle: 'BMW 3 Series (2022)',
        amount: '$450',
        bookingId: 'BK-8739',
      },
    },
    {
      id: '5',
      type: 'assignment',
      title: 'Driver Assigned',
      description: 'Alex K. assigned to booking #8742',
      user: 'Alex K.',
      location: 'Lagos, Nigeria',
      timestamp: '2025-01-08T13:00:00Z',
      status: 'completed',
      details: {
        bookingId: 'BK-8742',
      },
    },
    {
      id: '6',
      type: 'listing',
      title: 'New Listing',
      description: 'Tesla Model 3 added by Liaison Partner',
      user: 'Partner Auto',
      location: 'Lagos, Nigeria',
      timestamp: '2025-01-08T12:45:00Z',
      status: 'pending',
      details: {
        vehicle: 'Tesla Model 3 (2023)',
      },
    },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'booking':
        return <DirectionsCar />;
      case 'swap':
        return <SwapHoriz />;
      case 'cancellation':
        return <Cancel />;
      case 'listing':
        return <PersonAdd />;
      case 'assignment':
        return <Assignment />;
      default:
        return <DirectionsCar />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLoading(false);
  };

  return (
    <Card sx={{ height: 500 }}>
      <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Activity Feed
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            >
              <Refresh />
            </IconButton>
          </Box>
        </Box>

        {/* Activity List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <List disablePadding>
            {activities.map((activity, index) => (
              <React.Fragment key={activity.id}>
                <ListItem
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 48 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: `${activity.type === 'cancellation' ? theme.palette.error.main : theme.palette.primary.main}`,
                      }}
                    >
                      {getActivityIcon(activity.type)}
                    </Avatar>
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {activity.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {activity.description}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              {activity.user} • {activity.location}
                            </Typography>
                          </Box>
                          <Chip
                            label={activity.status}
                            size="small"
                            color={getStatusColor(activity.status) as any}
                            variant="outlined"
                            sx={{ height: 20 }}
                          />
                        </Box>
                        {activity.details && (
                          <Box sx={{ mt: 0.5 }}>
                            {activity.details.vehicle && (
                              <Typography variant="caption" color="text.secondary">
                                Vehicle: {activity.details.vehicle}
                              </Typography>
                            )}
                            {activity.details.amount && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                Amount: {activity.details.amount}
                              </Typography>
                            )}
                            {activity.details.bookingId && (
                              <Typography variant="caption" color="primary.main" sx={{ ml: 2 }}>
                                {activity.details.bookingId}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    }
                  />

                  <IconButton size="small">
                    <MoreVert />
                  </IconButton>
                </ListItem>
                {index < activities.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button fullWidth variant="text" size="small">
            View All Activities
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};